import React, { useEffect, useState } from "react";
import "@/App.css";
import { MessageCircle, Instagram, ChevronDown } from "lucide-react";
import { ImageCarousel } from "@/components/ImageCarousel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "+5549991670319";
const INSTAGRAM_HAAUU = "https://www.instagram.com/haauupoke/";
const INSTAGRAM_SUSHI = "https://www.instagram.com/sushiartchapeco/";
const INSTAGRAM_CELSO = "https://www.instagram.com/dion_c7/";

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          <button
            onClick={() => scrollToSection("hero")}
            className="text-2xl font-playfair font-bold text-white hover:text-accent-gold transition-colors"
            data-testid="nav-logo"
          >
            Celso Melara
          </button>
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("about")}
              className="text-sm font-manrope font-medium text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-about"
            >
              Sobre
            </button>
            <button
              onClick={() => scrollToSection("businesses")}
              className="text-sm font-manrope font-medium text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-businesses"
            >
              Negócios
            </button>
            <button
              onClick={() => scrollToSection("events")}
              className="text-sm font-manrope font-medium text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-events"
            >
              Eventos Privados
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="btn-primary"
              data-testid="nav-contact"
            >
              Contato
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const Hero = () => {
  return (
    <section
      id="hero"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/kzz8czlz_ChatGPT%20Image%2023%20de%20mar.%20de%202026%2C%2015_01_15.png')",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent"></div>
      </div>
      <div className="section-container relative z-10 py-32">
        <div className="max-w-3xl">
          <div className="fade-in-up">
            <span className="section-label" data-testid="hero-label">
              Empreendedor Visionário
            </span>
          </div>
          <h1
            className="hero-title text-5xl md:text-7xl font-bold text-white mt-6 mb-8 fade-in-up delay-100"
            data-testid="hero-title"
          >
            Construindo Excelência Através da Experiência e Visão
          </h1>
          <p
            className="hero-subtitle text-lg md:text-xl text-zinc-400 leading-relaxed mb-12 fade-in-up delay-200"
            data-testid="hero-subtitle"
          >
            Transformando conceitos culinários em experiências vibrantes e memoráveis através do Haauu Poke e Sushi Arte.
          </p>
          <div className="flex flex-wrap gap-4 fade-in-up delay-300">
            <button
              onClick={() => {
                const element = document.getElementById("businesses");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-primary"
              data-testid="hero-businesses-btn"
            >
              Ver Negócios
            </button>
            <button
              onClick={() => {
                const element = document.getElementById("contact");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-secondary"
              data-testid="hero-contact-btn"
            >
              Entre em Contato
            </button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-accent-gold" />
      </div>
    </section>
  );
};

const About = () => {
  return (
    <section
      id="about"
      className="py-24 md:py-32 border-t border-white/5"
      data-testid="about-section"
    >
      <div className="section-container">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-label" data-testid="about-label">
              Sobre Celso
            </span>
            <h2
              className="section-heading text-3xl md:text-5xl font-semibold text-white mt-4 mb-8"
              data-testid="about-heading"
            >
              Uma Jornada de Paixão e Inovação
            </h2>
            <div className="space-y-6 text-zinc-400 leading-relaxed" data-testid="about-bio">
              <p>
                Celso Melara é um empreendedor visionário cuja paixão pela inovação e sabor redefiniu a gastronomia contemporânea. Como criador do Haauu Poke e Sushi Arte, ele transformou conceitos culinários simples em experiências vibrantes e memoráveis que unem criatividade, qualidade e autenticidade.
              </p>
              <p>
                Impulsionado por uma busca incansável pela excelência, Celso construiu suas marcas do zero, guiado pela intuição, tomada de decisões ousadas e uma compreensão profunda do que as pessoas realmente desejam—não apenas em comida, mas em conexão e atmosfera. Sua jornada reflete resiliência, originalidade e a coragem de dar vida a novas ideias.
              </p>
              <p>
                Hoje, Celso Melara se destaca como um símbolo do empreendedorismo moderno, inspirando outros a transformar paixão em propósito e visão em realidade.
              </p>
            </div>
          </div>
          <div className="image-wrapper rounded-sm overflow-hidden">
           <img
               src="https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/kzz8czlz_ChatGPT%20Image%2023%20de%20mar.%20de%202026%2C%2015_01_15.png"
                   alt="Celso Melara"
                      className="w-full h-full object-cover"
                        data-testid="about-image" 
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Career = () => {
  const milestones = [
    {
      title: "Fundou o Haauu Poke",
      description:
        "Lançou um conceito inovador de poke que trouxe a culinária de inspiração havaiana para o mercado local com sabores vibrantes e ingredientes de qualidade.",
    },
    {
      title: "Estabeleceu o Sushi Arte",
      description:
        "Criou uma experiência premium de sushi focada em apresentação artística, qualidade excepcional e momentos gastronômicos memoráveis.",
    },
    {
      title: "Expandiu para Eventos Privados",
      description:
        "Começou a oferecer serviços exclusivos de catering para casamentos, aniversários e eventos corporativos, levando experiências de qualidade gastronômica para locais privados.",
    },
    {
      title: "Construiu um Legado Culinário",
      description:
        "Estabeleceu uma reputação de excelência na indústria alimentícia, inspirando empreendedores e amantes da gastronomia através da inovação e dedicação.",
    },
  ];

  return (
    <section
      id="career"
      className="py-24 md:py-32 border-t border-white/5"
      data-testid="career-section"
    >
      <div className="section-container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="section-label" data-testid="career-label">
              Marcos da Carreira
            </span>
            <h2
              className="section-heading text-3xl md:text-5xl font-semibold text-white mt-4"
              data-testid="career-heading"
            >
              Um Caminho de Conquistas
            </h2>
          </div>
          <div className="relative pl-8 space-y-12" data-testid="career-timeline">
            <div className="timeline-line"></div>
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`milestone-${index}`}>
                <div className="timeline-node"></div>
                <div className="pl-8">
                  <h3 className="text-2xl font-playfair font-semibold text-white mb-3">
                    {milestone.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Businesses = () => {
  const businesses = [
    {
      name: "Haauu Poke",
      description:
        "Uma celebração vibrante da culinária fresca de inspiração havaiana. O Haauu Poke oferece sabores ousados e apresentações coloridas que transformam cada refeição em uma experiência. Construído com ingredientes de qualidade e combinações criativas, é onde o saudável encontra o delicioso.",
      image:
        "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/48khxdmp_SaveClip.App_622940197_18068892122185121_6963028259089262396_n.jpg",
      instagram: INSTAGRAM_HAAUU,
      whatsappMessage: "Gostaria de mais informações sobre a franquia Haauu Poke.",
    },
    {
      name: "Sushi Arte",
      description:
        "Onde o artesanato culinário encontra a visão artística. Sushi Arte é uma experiência gastronômica premium que eleva o sushi tradicional a arte comestível. Cada peça é uma obra-prima, cada mordida um momento de perfeição. Projetado para quem aprecia qualidade, elegância e atenção aos detalhes.",
      image:
        "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/9sd7arg5_SaveClip.App_539087289_18052983965568284_7177191523971963048_n.jpg",
      instagram: INSTAGRAM_SUSHI,
      whatsappMessage: "Gostaria de mais informações sobre a franquia Sushi Arte.",
    },
  ];

  return (
    <section
      id="businesses"
      className="py-24 md:py-32 border-t border-white/5"
      data-testid="businesses-section"
    >
      <div className="section-container">
        <div className="text-center mb-16">
          <span className="section-label" data-testid="businesses-label">
            Empreendimentos
          </span>
          <h2
            className="section-heading text-3xl md:text-5xl font-semibold text-white mt-4"
            data-testid="businesses-heading"
          >
            Construindo Excelência Culinária
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {businesses.map((business, index) => (
            <div
              key={index}
              className="business-card overflow-hidden group"
              data-testid={`business-card-${index}`}
            >
              <div className="image-wrapper h-80">
               <img
                src={business.image}
                    alt={business.name}
                       className="w-full h-full object-cover"
                       data-testid={`business-image-${index}`}
                         loading="lazy"
                    />
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-playfair font-semibold text-white mb-4">
                  {business.name}
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-8">{business.description}</p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(business.whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 btn-primary"
                    data-testid={`business-whatsapp-${index}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Contato via WhatsApp
                  </a>
                  <a
                    href={business.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 btn-secondary"
                    data-testid={`business-instagram-${index}`}
                  >
                    <Instagram className="w-4 h-4" />
                    Ver no Instagram
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PrivateEvents = () => {
  const eventsWhatsappMessage = "Quero fazer um orçamento para meu evento privado.";
  
  return (
    <section
      id="events"
      className="py-24 md:py-32 border-t border-white/5 relative overflow-hidden"
      data-testid="events-section"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/14646752/pexels-photo-14646752.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500')",
        }}
      ></div>
      <div className="section-container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <span className="section-label" data-testid="events-label">
            Eventos Privados
          </span>
          <h2
            className="section-heading text-3xl md:text-5xl font-semibold text-white mt-4 mb-8"
            data-testid="events-heading"
          >
            Eleve Seus Momentos Especiais
          </h2>
          <p className="text-lg text-zinc-400 leading-relaxed mb-12" data-testid="events-description">
            De encontros íntimos a grandes celebrações, trazemos experiências culinárias de qualidade gastronômica para seus eventos privados. Seja um casamento, aniversário ou evento corporativo, cada detalhe é elaborado com precisão e cuidado para criar memórias inesquecíveis.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="glass p-8 border border-white/10" data-testid="event-type-weddings">
              <h3 className="text-xl font-playfair font-semibold text-white mb-3">Casamentos</h3>
              <p className="text-zinc-400">Catering elegante que torna seu dia especial verdadeiramente inesquecível</p>
            </div>
            <div className="glass p-8 border border-white/10" data-testid="event-type-birthdays">
              <h3 className="text-xl font-playfair font-semibold text-white mb-3">Aniversários</h3>
              <p className="text-zinc-400">Menus personalizados e apresentações para celebrações memoráveis</p>
            </div>
            <div className="glass p-8 border border-white/10" data-testid="event-type-corporate">
              <h3 className="text-xl font-playfair font-semibold text-white mb-3">Eventos Corporativos</h3>
              <p className="text-zinc-400">Serviço profissional que impressiona clientes e membros da equipe</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(eventsWhatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 btn-primary"
              data-testid="events-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Solicitar Orçamento
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Gallery = () => {
  const sushiImages = [
    { url: "https://images.pexels.com/photos/31415308/pexels-photo-31415308.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500", alt: "Sushi Rolls" },
    { url: "https://images.pexels.com/photos/31286350/pexels-photo-31286350.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500", alt: "Sushi Platter" },
    { url: "https://images.pexels.com/photos/34303217/pexels-photo-34303217.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500", alt: "Salmon Sushi" },
    { url: "https://images.pexels.com/photos/17533365/pexels-photo-17533365.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500", alt: "Assorted Sushi" },
    {
      url: "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/9sd7arg5_SaveClip.App_539087289_18052983965568284_7177191523971963048_n.jpg",
      alt: "Sushi Arte Special",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/3jfz95nk_349090735_1403082100261150_4470914524919728119_n.jpg",
      alt: "Sushi Arte",
    },
  ];

  const pokeImages = [
    { url: "https://images.pexels.com/photos/28992216/pexels-photo-28992216.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500", alt: "Poke Bowl Salmon" },
    { url: "https://images.pexels.com/photos/28701125/pexels-photo-28701125.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500", alt: "Poke Bowl Tuna" },
    {
      url: "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/48khxdmp_SaveClip.App_622940197_18068892122185121_6963028259089262396_n.jpg",
      alt: "Haauu Poke Special",
    },
    { url: "https://images.unsplash.com/photo-1768326119213-e0ad875083a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxzdXNoaSUyMHJvbGxzJTIwamFwYW5lc2UlMjBjdWlzaW5lJTIwcHJlbWl1bSUyMHF1YWxpdHl8ZW58MHx8fHwxNzc0NzkxMDM2fDA&ixlib=rb-4.1.0&q=85", alt: "Fresh Poke" },
    { url: "https://images.unsplash.com/photo-1660831830876-50a5512877af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxwb2tlJTIwYm93bCUyMGZyZXNoJTIwY29sb3JmdWwlMjBpbmdyZWRpZW50cyUyMHZpYnJhbnR8ZW58MHx8fHwxNzc0NzkxMDI4fDA&ixlib=rb-4.1.0&q=85", alt: "Colorful Poke Bowls" },
    {
      url: "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/071jz88s_haauu.jpg",
      alt: "Haauu Poke",
    },
  ];

  const staticImages = [
    {
      url: "https://images.pexels.com/photos/14646752/pexels-photo-14646752.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=400&w=500",
      alt: "Eventos Privados",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_celso-ventures/artifacts/3jfz95nk_349090735_1403082100261150_4470914524919728119_n.jpg",
      alt: "Sushi Arte Logo",
    },
  ];

  return (
    <section
      id="gallery"
      className="py-24 md:py-32 border-t border-white/5"
      data-testid="gallery-section"
    >
      <div className="section-container">
        <div className="text-center mb-16">
          <span className="section-label" data-testid="gallery-label">
            Galeria
          </span>
          <h2
            className="section-heading text-3xl md:text-5xl font-semibold text-white mt-4"
            data-testid="gallery-heading"
          >
            Momentos de Excelência
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4" data-testid="gallery-grid">
          <ImageCarousel images={sushiImages} title="Sushi Arte" />
          <ImageCarousel images={pokeImages} title="Haauu Poke" />
          {staticImages.map((image, index) => (
            <div
              key={index}
              className="image-wrapper h-96 rounded-sm overflow-hidden"
              data-testid={`gallery-image-${index + 2}`}
            >
              <img src={image.url} alt={image.alt} className="w-full h-full object-cover" loading="lazy"/>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  return (
    <section
      id="contact"
      className="py-24 md:py-32 border-t border-white/5"
      data-testid="contact-section"
    >
      <div className="section-container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="section-label" data-testid="contact-label">
              Entre em Contato
            </span>
            <h2
              className="section-heading text-3xl md:text-5xl font-semibold text-white mt-4"
              data-testid="contact-heading"
            >
              Vamos Iniciar uma Conversa
            </h2>
          </div>
          <div className="flex justify-center">
            <div className="max-w-md w-full">
              <h3 className="text-2xl font-playfair font-semibold text-white mb-8 text-center">
                Informações de Contato
              </h3>
              <div className="space-y-6">
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-zinc-400 hover:text-accent-gold transition-colors p-6 glass rounded-sm"
                  data-testid="contact-whatsapp-link"
                >
                  <div className="w-12 h-12 bg-whatsapp/10 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-whatsapp" />
                  </div>
                  <div>
                    <div className="text-sm text-zinc-500">WhatsApp</div>
                    <div className="text-white">Chef Celso</div>
                  </div>
                </a>
                <a
                  href={INSTAGRAM_CELSO}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-zinc-400 hover:text-accent-gold transition-colors p-6 glass rounded-sm"
                  data-testid="contact-instagram-link"
                >
                  <div className="w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-accent-gold" />
                  </div>
                  <div>
                    <div className="text-sm text-zinc-500">Instagram</div>
                    <div className="text-white">@celso_melara</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-white/5 py-12" data-testid="footer">
      <div className="section-container">
        <div className="text-center">
          <h3 className="text-2xl font-playfair font-bold text-white mb-4">Celso Melara</h3>
          <p className="text-zinc-500 mb-6">Empreendedor Visionário | Excelência Culinária</p>
          <div className="flex justify-center gap-6">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-accent-gold transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
            </a>
            <a
              href={INSTAGRAM_HAAUU}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-accent-gold transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>
          <p className="text-zinc-600 text-sm mt-8">
            © {new Date().getFullYear()} Celso Melara. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

const FloatingWhatsApp = () => {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, "")}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-8 right-8 z-40 w-16 h-16 bg-whatsapp rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
      data-testid="floating-whatsapp"
      style={{ marginBottom: "60px" }}
    >
      <MessageCircle className="w-7 h-7 text-white" />
    </a>
  );
};

function App() {
  return (
    <div className="App">
      <Navigation />
      <Hero />
      <About />
      <Career />
      <Businesses />
      <PrivateEvents />
      <Gallery />
      <Contact />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
